import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
  QuantityPerColorStepDto,
  QuantityPerSizePerColorStepDto,
  QuantityPerSizeStepDto,
  QuantityStepDto,
} from '@/api-sdk';
import { baseStyles } from '@/styles';
import { QuantityData } from '@/types';

import '../fg-configurator-step';
import './quantity/fg-quantity-per-color-table';
import './quantity/fg-quantity-per-size-per-color-table';
import './quantity/fg-quantity-per-size-table';
import './quantity/fg-quantity-table';
import { QuantityStepMixin } from './quantity/quantity-step-mixin';

@customElement('fg-configurator-step-quantity')
@localized()
export class FgConfiguratorStepQuantity extends QuantityStepMixin(LitElement) {
  renderMinimumQuantityText() {
    if (
      this.validationConfiguratorContext.showValidationMessages[
        this.quantityStep ?? ''
      ] &&
      this.validationMessage
    ) {
      return null;
    }

    const quantityData = this.quantityData as QuantityData;
    return quantityData?.minimumQuantity
      ? html`
          <p class="minimum-quantity-text">
            ${t(
              !quantityData.unprinted
                ? 'labels.minimumQuantityPrinted'
                : 'labels.minimumQuantityUnprinted',
              {
                quantity: quantityData?.minimumQuantity,
              },
            )}${quantityData?.hspMaximumQuantity
              ? `, ${t('labels.maxQuickDelivery', {
                  count: quantityData?.hspMaximumQuantity,
                })}
              `
              : nothing}
          </p>
        `
      : nothing;
  }

  renderQuantitiesTable() {
    switch (this.quantityStep) {
      case 'quantity_per_color': {
        const quantityData = this.quantityData as QuantityPerColorStepDto;
        return html`
          <fg-quantity-per-color-table
            .options=${quantityData?.options}
            .hspMaximumQuantity=${quantityData.hspMaximumQuantity}
            .totalQuantity=${quantityData?.totalQuantity}
            .totalPrice=${quantityData?.totalPrice}
            .loading=${this.loading}
          ></fg-quantity-per-color-table>
        `;
      }

      case 'quantity_per_size': {
        const quantityData = this.quantityData as QuantityPerSizeStepDto;
        return html`
          <fg-quantity-per-size-table
            .options=${quantityData?.options}
            .totalQuantity=${quantityData?.totalQuantity}
            .hspMaximumQuantity=${quantityData?.hspMaximumQuantity}
            .totalPrice=${quantityData?.totalPrice}
            .loading=${this.loading}
          ></fg-quantity-per-size-table>
        `;
      }
      case 'quantity_per_size_per_color': {
        const quantityData = this
          .quantityData as QuantityPerSizePerColorStepDto;
        return html`
          <fg-quantity-per-size-per-color-table
            .colors=${this.colors}
            .options=${quantityData?.options}
            .totalQuantity=${quantityData?.totalQuantity}
            .hspMaximumQuantity=${quantityData?.hspMaximumQuantity}
            .totalPrice=${quantityData?.totalPrice}
            .loading=${this.loading}
          ></fg-quantity-per-size-per-color-table>
        `;
      }
      case 'quantity': {
        const quantityData = this.quantityData as QuantityStepDto;
        return html`
          <fg-quantity-table
            .options=${quantityData?.quantityOptions}
            .loading=${this.loading}
            .customQuantityOption=${quantityData?.customQuantityOption}
          ></fg-quantity-table>
        `;
      }
    }
  }

  render() {
    return html`
      <fg-configurator-step .stepTitle=${t('headers.chooseQuantity')}
      .validationMessage=${this.validationMessage}
      .showValidationMessage=${this.validationConfiguratorContext.showValidationMessages[this.quantityStep ?? ''] || false}
      ></fg-configurator-step>

        ${this.renderMinimumQuantityText()}

        ${
          this.quantityData
            ? html`
                <div class="quantities-wrapper">
                  ${this.renderQuantitiesTable()}
                </div>
              `
            : nothing
        }

        <p class="excluding-tax-text">
          ${t('labels.pricesExclVat')}
        </p>
      </fg-configurator-step>
    `;
  }

  static styles = [
    baseStyles,
    css`
      .minimum-quantity-text {
        margin-top: 15px;
      }

      .excluding-tax-text {
        margin-top: 10px;
      }

      .quantities-wrapper {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        margin-top: 10px;
      }

      .quantities-wrapper:not(.collapsible) {
        border-radius: 10px;
      }
    `,
  ];
}
